// HR candidate drawer — "Messages" tab: the candidate's full WhatsApp thread
// with the HR's company (notifications we sent, Maya's replies, and the
// candidate's own messages), oldest → newest. The reply composer lives in the
// drawer footer (see WhatsappReplyComposer) so it's always visible without
// scrolling; this component only renders the thread + "load older" pagination.
//
// The drawer owns the page-1 (newest) fetch so it can show the unread dot on
// the tab before it's opened; this component receives that result and owns
// only the "load older" pagination on top of it.
//
// Pagination convention (per handoff — "older pages reachable by scrolling
// upward"): page 1 is the NEWEST chunk; higher pages are OLDER. Each page is
// oldest → newest internally, so an older page is prepended verbatim.
import { useEffect, useMemo, useRef, useState } from "react";
import {
    AlertTriangle,
    Check,
    CheckCheck,
    Clock,
    Loader2,
    MessageSquareText,
} from "lucide-react";
import { EmptyState } from "@component/empty-state";
import { getCandidateWhatsappThread } from "@service/whatsapp";
import type {
    WhatsappMessageStatus,
    WhatsappThreadMessage,
    WhatsappThreadResult,
} from "@type/whatsapp";
import { parseFormat } from "@util/date";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";

export type MessagesTabError = "forbidden" | "error" | null;

interface MessagesTabContentProps {
    candidateId: string;
    /** Candidate display name for the inbound (left) bubble label. */
    candidateName?: string;
    /** Page-1 (newest) result fetched by the drawer; null while loading. */
    initialResult: WhatsappThreadResult | null;
    initialError: MessagesTabError;
    isInitialLoading: boolean;
}

// "assessment_invitation" → "Assessment invitation"
function formatEvent(event: string): string {
    const spaced = event.replace(/_/g, " ").trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// Outbound delivery state → tick/label. Read = blue double-tick, delivered =
// gray double-tick, queued/sent = single tick, failure = amber warning.
function DeliveryStatus({
    status,
    errorCode,
}: {
    status: WhatsappMessageStatus;
    errorCode: string | null;
}) {
    if (status === "failed" || status === "undelivered") {
        return (
            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-3 h-3" />
                {status === "failed" ? "Failed" : "Undelivered"}
                {errorCode && <span className="text-amber-500">· {errorCode}</span>}
            </span>
        );
    }
    if (status === "read") {
        return <CheckCheck className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />;
    }
    if (status === "delivered") {
        return <CheckCheck className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />;
    }
    if (status === "sent" || status === "simulated") {
        // "simulated" (the /whatsapp/simulate harness) reads as a plain sent
        // message — single tick, no clock.
        return <Check className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />;
    }
    // queued (or any other pre-sent state)
    return <Clock className="w-3 h-3 text-gray-400 dark:text-gray-500" />;
}

function MessageRow({
    message,
    candidateName,
}: {
    message: WhatsappThreadMessage;
    candidateName?: string;
}) {
    const isOutbound = message.direction === "outbound";
    const isHrReply = message.event === "hr_reply";
    // An HR reply is authored by the HR, not Maya — don't tag it with Maya's
    // name or an "event" chip; only true template notifications get the chip.
    const isTemplate = message.message_type === "template" && !isHrReply;
    const time = message.created_at
        ? parseFormat(message.created_at, "D MMM YYYY, h:mm A")
        : "";
    const senderLabel = isOutbound
        ? isHrReply
            ? "You"
            : "Maya"
        : candidateName || "Candidate";

    return (
        <div className={cn("flex flex-col", isOutbound ? "items-end" : "items-start")}>
            <div className="max-w-[80%]">
                <div
                    className={cn(
                        "flex items-center gap-2 mb-1",
                        isOutbound ? "justify-end" : "justify-start"
                    )}
                >
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {senderLabel}
                    </span>
                    {isTemplate && message.event && (
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900">
                            {/* Prefer the BE-curated event_label ("System Reminder");
                                older payloads without it fall back to the slug. */}
                            {message.event_label || formatEvent(message.event)}
                        </span>
                    )}
                </div>
                <div
                    className={cn(
                        "rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap break-words",
                        isOutbound
                            ? isTemplate
                                ? "bg-blue-50 text-gray-800 border border-blue-100 rounded-tr-sm dark:bg-blue-950/30 dark:text-gray-100 dark:border-blue-900/60"
                                : "bg-blue-100 text-gray-900 border border-blue-200 rounded-tr-sm dark:bg-blue-950/40 dark:text-gray-100 dark:border-blue-900/60"
                            : "bg-gray-100 text-gray-900 rounded-tl-sm dark:bg-gray-800 dark:text-gray-100"
                    )}
                >
                    {message.body?.trim() ? (
                        message.body
                    ) : (
                        <span className="italic opacity-70">(no message body)</span>
                    )}
                </div>
                <div
                    className={cn(
                        "flex items-center gap-1.5 mt-1 text-xs text-gray-400 dark:text-gray-500",
                        isOutbound ? "justify-end" : "justify-start"
                    )}
                >
                    <span>{time}</span>
                    {isOutbound && (
                        <>
                            <span aria-hidden>·</span>
                            <DeliveryStatus
                                status={message.status}
                                errorCode={message.error_code}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function MessagesTabContent({
    candidateId,
    candidateName,
    initialResult,
    initialError,
    isInitialLoading,
}: MessagesTabContentProps) {
    // Seeded from the drawer's page-1 result so the first render already shows
    // the thread (no empty-state flash before the sync effect runs).
    const [messages, setMessages] = useState<WhatsappThreadMessage[]>(
        () => initialResult?.messages ?? []
    );
    const [page, setPage] = useState<number>(
        () => initialResult?.pagination.page ?? 1
    );
    const [totalPages, setTotalPages] = useState<number>(
        () => initialResult?.pagination.total_pages ?? 1
    );
    const [loadingOlder, setLoadingOlder] = useState<boolean>(false);
    const [olderError, setOlderError] = useState<string | null>(null);

    const scrollRef = useRef<HTMLDivElement>(null);
    const didInitialScroll = useRef<boolean>(false);

    // Seed local state from the drawer's page-1 (newest) result.
    useEffect(() => {
        if (!initialResult) return;
        setMessages(initialResult.messages);
        setPage(initialResult.pagination.page || 1);
        setTotalPages(initialResult.pagination.total_pages || 1);
        didInitialScroll.current = false;
    }, [initialResult]);

    // Land at the newest message on first render of a freshly loaded thread.
    useEffect(() => {
        if (didInitialScroll.current || messages.length === 0) return;
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
        didInitialScroll.current = true;
    }, [messages]);

    const hasOlder = page < totalPages;

    async function loadOlder() {
        if (loadingOlder || !hasOlder) return;
        setLoadingOlder(true);
        setOlderError(null);
        const el = scrollRef.current;
        const prevHeight = el?.scrollHeight ?? 0;
        try {
            const older = await getCandidateWhatsappThread(candidateId, page + 1);
            // Older page (all older than current) prepended verbatim — each page
            // is already oldest → newest.
            setMessages((cur) => [...older.messages, ...cur]);
            setPage(older.pagination.page || page + 1);
            setTotalPages(older.pagination.total_pages || totalPages);
            // Keep the reading position steady after prepending older content.
            requestAnimationFrame(() => {
                const node = scrollRef.current;
                if (node) node.scrollTop = node.scrollHeight - prevHeight;
            });
        } catch (err) {
            setOlderError("Could not load older messages. Please try again.");
            catchErrorLog(`[whatsapp-thread] load older failed: ${err}`);
        } finally {
            setLoadingOlder(false);
        }
    }

    const isEmpty = useMemo(
        () => !isInitialLoading && !initialError && messages.length === 0,
        [isInitialLoading, initialError, messages.length]
    );

    if (isInitialLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
        );
    }

    if (initialError === "forbidden") {
        return (
            <EmptyState
                icon={<MessageSquareText className="h-12 w-12 text-gray-300 dark:text-gray-600" />}
                title="No access to these messages"
                description="You can only view the WhatsApp thread for candidates of your own company."
            />
        );
    }

    if (initialError === "error") {
        return (
            <EmptyState
                icon={<AlertTriangle className="h-12 w-12 text-gray-300 dark:text-gray-600" />}
                title="Couldn't load messages"
                description="Something went wrong loading this candidate's WhatsApp thread. Try reopening the tab."
            />
        );
    }

    if (isEmpty) {
        return (
            <EmptyState
                icon={<MessageSquareText className="h-12 w-12 text-gray-300 dark:text-gray-600" />}
                title="No messages yet"
                description="Once this candidate exchanges WhatsApp messages with your company — notifications, Maya's replies, or their own questions — the conversation will show up here."
            />
        );
    }

    return (
        <div
            ref={scrollRef}
            className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-4"
        >
            {hasOlder && (
                <div className="flex justify-center pt-1">
                    <button
                        type="button"
                        onClick={loadOlder}
                        disabled={loadingOlder}
                        className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-gray-700 px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-60"
                    >
                        {loadingOlder && <Loader2 className="w-3 h-3 animate-spin" />}
                        {loadingOlder ? "Loading…" : "Load older messages"}
                    </button>
                </div>
            )}
            {olderError && (
                <p className="text-center text-xs text-amber-600 dark:text-amber-400">
                    {olderError}
                </p>
            )}
            {messages.map((m) => (
                <MessageRow key={m.id} message={m} candidateName={candidateName} />
            ))}
        </div>
    );
}
